import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'
import { addDays } from 'date-fns'

import { Button, ButtonColor, ButtonVariant, Modal, useTheme } from '@makyoapp/makyo-ui'

import useAuth from '@/hooks/useAuth'
import { useDateFmt } from '@/hooks/useDateFmt'
import { SaveButton } from '@/components/SaveButton'

interface IRestoreAccountModal {
  onHide: () => void
  onDelete: () => void
  onRestore: () => void
  isLoading: boolean
}

/**
 * RestoreAccountModal is a function component that displays a modal which allows a user to restore or permanently delete their account.
 *
 * @param {Object} props - The component's props.
 * @param {function} props.onHide - The function to call when the user clicks the 'Hide' button.
 * @param {function} props.onDelete - The function to call when the user clicks the 'Delete' button.
 * @param {function} props.onRestore - The function to call when the user clicks the 'Restore' button.
 * @param {boolean} props.isLoading - A boolean indicating whether a request is currently in progress.
 *
 * @returns {JSX.Element} A Modal element containing the restore account modal.
 */
function RestoreAccountModal(props: IRestoreAccountModal) {
  const { t } = useTranslation()
  const { userData } = useAuth()
  const willBeDeletedOn = userData?.deletedAt ? addDays(userData?.deletedAt, 14) : undefined

  const [isDelete, setIsDelete] = useBoolean(false)

  const { state: themeState } = useTheme()
  const dateFmtDefault = useDateFmt()

  return (
    <Modal
      className={themeState.mode === 'dark' ? 'dark' : 'light'}
      show={true}
      onHide={props.isLoading ? undefined : props.onHide}
    >
      <Modal.Header onCloseClick={props.isLoading ? undefined : props.onHide}>
        {/* Depending on the isDelete state, change the modal title */}
        {!isDelete ? t('Restore account') : t('Delete permanently')}
      </Modal.Header>
      <Modal.Body>
        <div className='modal-sm'>
          {!isDelete ? (
            <>
              <div className='text-base mb-2'>Welcome back to Makyo!</div>
              <div>
                {userData && userData?.deletedAt && willBeDeletedOn ? (
                  /* If user data exists and the account deletion date is not null,
            show the restore account message with the deletion date and deadline */
                  <Trans
                    i18nKey='accountRestoreInfoWithDate'
                    values={{
                      date: dateFmtDefault(userData.deletedAt),
                      before: dateFmtDefault(willBeDeletedOn),
                    }}
                  >
                    Your account has been deleted on <strong>{dateFmtDefault(userData.deletedAt)}</strong>. You can
                    restore it before <strong>{dateFmtDefault(willBeDeletedOn)}</strong>. Would you like to restore it
                    now?
                  </Trans>
                ) : (
                  /* If user data or the account deletion date is null, 
            show the general restore account message */
                  <Trans i18nKey='accountRestoreInfo'>
                    Your account has been deleted but it&apos;s not too late to restore it. Would you like to restore it
                    now?
                  </Trans>
                )}
              </div>
            </>
          ) : (
            <>
              {/* If isDelete state is true, show the delete account permanently message */}
              <div className='text-base mb-2'>{t("Are you sure you'd like to delete your account permanently")}</div>
              <div className='text-light'>
                {t(
                  "This can't be reversed. All your apps, databases, organizations  and teams will be permanently deleted and you won't be able to restore them."
                )}
              </div>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        // Change the alignment of the modal footer based on the isDelete state
        className={clsx(isDelete ? 'justify-end' : 'justify-between')}
      >
        {isDelete && (
          // If isDelete state is true, show a 'Yes, delete' button
          <SaveButton
            colorStyle={ButtonColor.Danger}
            isLoading={props.isLoading}
            onClick={props.onDelete}
            label={t('Yes, delete')}
          />
        )}
        {!isDelete && (
          <>
            {/* If isDelete state is false, show a 'Cancel' button */}
            <Button disabled={props.isLoading} variant={ButtonVariant.Outlined} onClick={props.onHide}>
              {t('Cancel')}
            </Button>
            <div className='flex-c'>
              {/* If isDelete state is false, show a 'Delete permanently' button */}
              <Button
                colorStyle={ButtonColor.Danger}
                onClick={() => {
                  setIsDelete(true)
                }}
              >
                {t('Delete permanently')}
              </Button>
              {/* If isDelete state is false, show a 'Yes, restore' button */}
              <SaveButton isLoading={props.isLoading} onClick={props.onRestore} label={t('Yes, restore')} />
            </div>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default RestoreAccountModal
